import { applyPct, mulQ4, toSafe } from "./arith";
import { isIsoDate } from "./dates";
import { DomainError } from "./errors";

/** Tipos do RF-CAD-04: Taxas (± % sobre preço ou custo), Por período (taxa com vigência), Customizada (por item). */
export const PRICE_LIST_KINDS = ["rate", "period", "custom"] as const;
export type PriceListKind = (typeof PRICE_LIST_KINDS)[number];
export const PRICE_LIST_BASES = ["price", "cost"] as const;
export type PriceListBase = (typeof PRICE_LIST_BASES)[number];

/** −100% (preço zero) a +1000% em pct4. */
export const MIN_PRICE_RATE_PCT4 = -1_000_000;
export const MAX_PRICE_RATE_PCT4 = 10_000_000;

export type PriceListItemRule = {
  priceCents?: number | null;
  ratePct4?: number | null;
  minQtyQ4?: number | null;
};
export type PriceListRule = {
  kind: PriceListKind;
  ratePct4?: number | null;
  base: PriceListBase;
  validFrom?: string | null;
  validTo?: string | null;
  /** Ausente = ativa. */
  active?: boolean;
  /** Item do produto na lista (só vale na customizada). */
  item?: PriceListItemRule | null;
};

export type PriceSource = "product" | "list_rate" | "item_price" | "item_rate";
/** Por que a lista não se aplicou (só com `source = "product"`). */
export type PriceReason = "no_list" | "inactive" | "out_of_period" | "no_item" | "below_min_qty";
export type PriceExplanation = { priceCents: number; source: PriceSource; reason?: PriceReason };

export type ResolvePriceInput = {
  /** Preço de venda do produto (centavos). */
  basePrice: number;
  /** Preço de custo do produto (centavos). */
  cost: number;
  list?: PriceListRule | null;
  qty: number;
  /** Data do documento, AAAA-MM-DD. */
  date: string;
};

const assertInt = (v: number, what: string) => {
  if (!Number.isSafeInteger(v)) throw new DomainError("not_integer", `${what} deve ser inteiro: ${v}`);
};
const assertDate = (d: string | null | undefined) => {
  if (d != null && !isIsoDate(d)) throw new DomainError("invalid_date", `data inválida: ${d}`);
};

/** Teto de preço/custo em centavos (R$ 10 bilhões), validado nos contratos. */
export const MAX_PRICE_CENTS = 1_000_000_000_000;

/** base ± %, arredondado a centavos (meio para cima, `applyPct`), nunca negativo. Soma em BigInt: acima de 2^53 = invalid_money. */
const withRate = (value: number, ratePct4: number | null | undefined) =>
  Math.max(0, toSafe(BigInt(value) + BigInt(applyPct(value, ratePct4 ?? 0)), "invalid_money"));

/**
 * Preço efetivo do item (05 §2), com a origem para a prévia da tela:
 * 1. lista ausente, inativa ou fora da vigência (inclusiva) ⇒ preço do produto;
 * 2. customizada com item e `qty ≥ minQty` ⇒ preço do item (ou base ± `item.ratePct4`); sem item ⇒ produto;
 * 3. taxa/período ⇒ (custo ou preço) + `applyPct(…, ratePct4)`.
 */
export function explainPrice(input: ResolvePriceInput): PriceExplanation {
  const { basePrice, cost, list, qty, date } = input;
  assertInt(basePrice, "preço");
  assertInt(cost, "custo");
  assertInt(qty, "quantidade");
  assertDate(date);
  const product = (reason: PriceReason): PriceExplanation => ({
    priceCents: basePrice,
    source: "product",
    reason,
  });
  if (!list) return product("no_list");
  assertDate(list.validFrom);
  assertDate(list.validTo);
  if (list.active === false) return product("inactive");
  if ((list.validFrom && date < list.validFrom) || (list.validTo && date > list.validTo))
    return product("out_of_period");
  const baseValue = list.base === "cost" ? cost : basePrice;
  if (list.kind !== "custom") return { priceCents: withRate(baseValue, list.ratePct4), source: "list_rate" };
  const item = list.item;
  if (!item || (item.priceCents == null && item.ratePct4 == null)) return product("no_item");
  if (item.minQtyQ4 != null && qty < item.minQtyQ4) return product("below_min_qty");
  if (item.priceCents != null) return { priceCents: item.priceCents, source: "item_price" };
  return { priceCents: withRate(baseValue, item.ratePct4), source: "item_rate" };
}

export const resolvePrice = (input: ResolvePriceInput): number => explainPrice(input).priceCents;

/** Regra de preço no item (RF-CAD-04): lista do pedido > lista da loja > preço do produto (null). */
export const effectivePriceListId = (
  orderListId: string | null | undefined,
  storeListId: string | null | undefined,
): string | null => orderListId ?? storeListId ?? null;

/** Desconto do item (05 §2): `unit = list − applyPct(list, desc)`, `total = mulQ4(unit, qty)`. */
export function itemPricing(
  listPriceCents: number,
  discountPct4: number,
  qtyQ4: number,
): { unitCents: number; totalCents: number } {
  const unitCents = listPriceCents - applyPct(listPriceCents, discountPct4);
  return { unitCents, totalCents: mulQ4(unitCents, qtyQ4) };
}
